import React, { useState, useEffect } from 'react';
import { Container, Card, CardContent, CardHeader, Typography, Alert, Box, Button, Grid, Divider, CircularProgress, Chip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { agentService } from '../services/api';
import AgentFormWithTabs from '../components/AgentFormWithTabs';


const AgentDetailsPage = () => {
  const { id } = useParams();
  
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const data = await agentService.getAgentById(id);
        setAgent(data);
      } catch (err) {
        console.error('Error fetching agent:', err);
        setError('Failed to load agent details');
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [id]);

  const handleToggleEdit = () => {
    setShowEditForm(!showEditForm);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  if (!agent) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Agent not found</Alert>
      </Container>
    );
  }

  if (showEditForm) {
    return <AgentFormWithTabs mode="edit" agent={agent} />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card elevation={2}>
        <CardHeader
          title={agent.name}
          titleTypographyProps={{ variant: 'h4', component: 'h1' }}
          action={
            <Button
              variant="contained"
              onClick={handleToggleEdit}
            >
              Edit Agent
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {agent.name}
              </Typography>
              
              {agent.description && (
                <Typography variant="body1" gutterBottom>
                  <strong>Description:</strong> {agent.description}
                </Typography>
              )}
              
              <Typography variant="body1" gutterBottom>
                <strong>Status:</strong> 
                <Chip 
                  label={agent.status} 
                  color={agent.status === 'active' ? 'success' : 'default'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              
              <Typography variant="body1" gutterBottom>
                <strong>Created:</strong> {new Date(agent.createdAt).toLocaleDateString()}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" gutterBottom>
                <strong>Voice ID:</strong> {agent.voiceId}
              </Typography>
              
              {agent.llmConfig && (
                <>
                  <Typography variant="body1" gutterBottom>
                    <strong>Model:</strong> {agent.llmConfig.model || agent.llmConfig.s2sModel}
                  </Typography>
                  
                  <Typography variant="body1" gutterBottom>
                    <strong>Temperature:</strong> {agent.llmConfig.modelTemperature}
                  </Typography>
                  
                  <Typography variant="body1" gutterBottom>
                    <strong>High Priority:</strong> {agent.llmConfig.modelHighPriority ? 'Yes' : 'No'}
                  </Typography>
                </>
              )}
              
              {agent.webhookConfig?.url && (
                <Typography variant="body1" gutterBottom>
                  <strong>Webhook URL:</strong> {agent.webhookConfig.url}
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AgentDetailsPage;
